import { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Printer, Search, FileText, Package, Calendar,
  RefreshCw, Filter, Building2, User
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PrintHistoryPage = () => {
  const { token } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [stations, setStations] = useState([]);
  const [filterStation, setFilterStation] = useState('');

  useEffect(() => {
    fetchStations();
  }, []);
  
  useEffect(() => {
    if (token) {
      fetchPrintHistory();
    }
  }, [token, filterType, filterStation]);

  const fetchStations = async () => {
    try {
      const response = await axios.get(`${API_URL}/stations`);
      setStations(response.data);
    } catch (error) {
      console.error('Failed to fetch stations:', error);
    }
  };

  const fetchPrintHistory = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      let url = `${API_URL}/print-history`;
      const params = new URLSearchParams();
      if (filterType) params.append('document_type', filterType);
      if (filterStation) params.append('station_id', filterStation);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch print history:', error);
      if (error.response?.status !== 401) {
        toast.error('Failed to load print history');
      }
    } finally {
      setLoading(false);
    }
  };

  const getTypeBadge = (type) => {
    const config = {
      ticket: { color: 'bg-blue-100 text-blue-700', icon: FileText },
      luggage: { color: 'bg-purple-100 text-purple-700', icon: Package },
    };
    const { color, icon: Icon } = config[type] || { color: 'bg-slate-100 text-slate-700', icon: FileText };
    
    return (
      <Badge className={color}>
        <Icon className="h-3 w-3 mr-1" />
        {type?.charAt(0).toUpperCase() + type?.slice(1)}
      </Badge>
    );
  };

  const filteredHistory = history.filter(item => {
    const matchesSearch = 
      item.document_ref?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.printed_by_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.station_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  // Calculate stats
  const ticketPrints = history.filter(h => h.document_type === 'ticket').length;
  const luggagePrints = history.filter(h => h.document_type === 'luggage').length;
  const uniquePrinters = [...new Set(history.map(h => h.printed_by))].length;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="print-history-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">
              Print History
            </h1>
            <p className="text-slate-500 mt-1">
              Track ticket and luggage receipt printing
            </p>
          </div>
          <Button onClick={fetchPrintHistory} variant="outline" data-testid="refresh-history-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="card-panashe">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Printer className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{history.length}</p>
                  <p className="text-sm text-slate-500">Total Prints</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="card-panashe">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <FileText className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{ticketPrints}</p>
                  <p className="text-sm text-slate-500">Ticket Prints</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="card-panashe">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                  <Package className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{luggagePrints}</p>
                  <p className="text-sm text-slate-500">Luggage Prints</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="card-panashe">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-panashe-gold/20 rounded-full flex items-center justify-center">
                  <User className="h-5 w-5 text-panashe-blue" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{uniquePrinters}</p>
                  <p className="text-sm text-slate-500">Staff Printed</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="card-panashe">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search by reference, staff, or station..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="search-history-input"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="px-3 py-2 border rounded-md text-sm"
                  data-testid="filter-type-select"
                >
                  <option value="">All Types</option>
                  <option value="ticket">Tickets</option>
                  <option value="luggage">Luggage</option>
                </select>
                <select
                  value={filterStation}
                  onChange={(e) => setFilterStation(e.target.value)}
                  className="px-3 py-2 border rounded-md text-sm"
                  data-testid="filter-station-select"
                >
                  <option value="">All Stations</option>
                  {stations.map(station => (
                    <option key={station.id} value={station.id}>{station.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Print History Table */}
        <Card className="card-panashe">
          <CardHeader className="border-b">
            <CardTitle className="font-heading flex items-center gap-2">
              <Printer className="h-5 w-5" />
              Print Records ({filteredHistory.length} entries)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <div className="spinner mx-auto mb-4" />
                <p className="text-slate-500">Loading print history...</p>
              </div>
            ) : filteredHistory.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <Printer className="h-12 w-12 mx-auto mb-4 opacity-30" />
                <p>No print history found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-4 font-medium text-slate-600">Type</th>
                      <th className="text-left p-4 font-medium text-slate-600">Reference</th>
                      <th className="text-left p-4 font-medium text-slate-600">Printed By</th>
                      <th className="text-left p-4 font-medium text-slate-600">Station</th>
                      <th className="text-left p-4 font-medium text-slate-600">Date/Time</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredHistory.map((item, index) => (
                      <tr 
                        key={item.id || index} 
                        className="hover:bg-slate-50"
                        data-testid={`print-entry-${index}`}
                      >
                        <td className="p-4">
                          {getTypeBadge(item.document_type)}
                        </td>
                        <td className="p-4">
                          <span className="font-mono text-sm bg-slate-100 px-2 py-1 rounded">
                            {item.document_ref}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-panashe-blue rounded-full flex items-center justify-center text-white text-sm font-bold">
                              {item.printed_by_name?.charAt(0) || '?'}
                            </div>
                            <span className="text-slate-900">{item.printed_by_name || 'Unknown'}</span>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-1 text-slate-600">
                            <Building2 className="h-4 w-4" />
                            {item.station_name || 'N/A'}
                          </div>
                        </td>
                        <td className="p-4 text-slate-500">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {item.created_at ? 
                              format(parseISO(item.created_at), 'MMM d, yyyy HH:mm') : 
                              'Unknown'
                            }
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default PrintHistoryPage;
